import React, { useCallback, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import styles from "./Style.module.css";
import { Typography } from "@mui/material";
import ShadowBox from "../../../../components/ShadowBox/ShadowBox";
import { removeUnderScore } from "../../../../helper/Helper";
import NoDataFound from "../NoDataFound/NoDataFound";
import { FileDownloadOutlined } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const defaultData = [
  { date: "12/02/2025", Consumption: 500, Refills: 600 },
  { date: "13/02/2025", Consumption: 300, Refills: 200 },
  { date: "14/02/2025", Consumption: 800, Refills: 900 },
  { date: "15/02/2025", Consumption: 500, Refills: 1100 },
  { date: "16/02/2025", Consumption: 600, Refills: 700 },
  { date: "17/02/2025", Consumption: 700, Refills: 500 },
  { date: "18/02/2025", Consumption: 300, Refills: 400 },
  { date: "19/02/2025", Consumption: 400, Refills: 600 },
];

const defaultColors = {
  Consumption: "#f4a621",
  Refills: "#1f78c1",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className={styles.tooltipContainer}>
      <strong className={styles.tooltipDate}>{label}</strong>
      {payload.map((entry) => (
        <div key={entry.name} className={styles.tooltipItem}>
          <span
            className={styles.tooltipDot}
            style={{ backgroundColor: entry.color }}
          ></span>
          {removeUnderScore(entry?.name)}: <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ colors }) => {
  return (
    <div className={styles.legendContainer}>
      {Object.keys(colors).map((key) => (
        <div key={key} className={styles.legendItem}>
          <span
            className={styles.legendDot}
            style={{ backgroundColor: colors[key] }}
          ></span>
          {removeUnderScore(key)}
        </div>
      ))}
    </div>
  );
};

const GroupedBarChart = ({
  data = defaultData,
  colors = defaultColors,
  dataKey = "date",
  title = "Medicine Consumption and Refills",
  className = "",
  style = {},
  height = 250,
  minWidth = 1200,
  isImgDownload = false,
  isExpand = false,
  onExpand,
  downloadTitle = "",
  isExport,
  handleExport,
}) => {
  const chartRef = useRef();
  const keys = Object.keys(data[0] || {}).filter((key) => key !== dataKey);
  const barGroupWidth = 90;
  const chartWidth = data?.length * barGroupWidth;
  const minChartWidth = minWidth;

  const NO_SCROLL_MAX = 15;
  const isScrollable = (data?.length || 0) > NO_SCROLL_MAX;

  const GROUP_WIDTH = 80;
  const scrollWidth = (data?.length || 0) * GROUP_WIDTH;

  const handleDownload = useCallback(() => {
    if (!chartRef.current) return;

    const svg = chartRef.current.container?.firstChild;
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);

    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const paddingTop = 40;
      const canvas = document.createElement("canvas");
      canvas.width = svg.clientWidth || 1200;
      canvas.height = (svg.clientHeight || 600) + paddingTop;
      const ctx = canvas.getContext("2d");

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#636578";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(title || "Medicine Consumption and Refills", 10, 10);

      ctx.drawImage(img, 0, paddingTop);

      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `${title || "chart"}.png`;
      a.click();
    };
    img.src = url;
  }, [title]);

  return (
    <ShadowBox className={`${styles.wrapper} ${className}`} style={style}>
      <div className={styles.header}>
        <div className={styles.dFlex}>
          <Typography variant="h5" className="heading">
            {title}
          </Typography>
          {isExpand && (
            <OpenInNewIcon
              onClick={onExpand}
              color="primary"
              size="small"
              className={styles.expandIcon}
            />
          )}
          {isImgDownload && (
            <FileDownloadOutlined
              onClick={handleDownload}
              color="primary"
              size="small"
              className={styles.expandIcon}
            />
          )}
        </div>

        <div>
          {isExport && (
            <div className={styles.dFlex2}>
              <Typography variant="h5" className="heading">
                {downloadTitle}
              </Typography>
              <FileDownloadOutlined
                onClick={handleExport}
                color="primary"
                size="small"
                className={styles.expandIcon}
              />
            </div>
          )}
        </div>
      </div>
      
      <div className={styles.dFlex}>
        <CustomLegend colors={colors} />
      </div>
      {data?.length > 0 ? (
        <>
          <div className={styles.scrollable}>
            <div
              style={{
                overflowX: isScrollable ? "auto" : "hidden",
                overflowY: "hidden",
              }}
            >
              <div style={{ width: isScrollable ? scrollWidth : "100%" }}>
                <ResponsiveContainer width="100%" height={height}>
                  <BarChart
                    height={height}
                    ref={chartRef}
                    data={data}
                    barGap={8}
                    margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                    barCategoryGap={isScrollable ? "20%" : "30%"}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#bbbbbb" />
                    <XAxis
                      dataKey={dataKey}
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        value.charAt(0).toUpperCase() + value.slice(1)
                      }
                    />
                    <YAxis />
                    <Tooltip cursor={false} content={<CustomTooltip />} />
                    {keys.map((key) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        fill={colors[key]}
                        barSize={20}
                      >
                        <LabelList
                          dataKey={key}
                          position="top"
                          style={{
                            fill: "#161616",
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      ) : (
        <NoDataFound />
      )}
    </ShadowBox>
  );
};

export default GroupedBarChart;
